import React, { Component } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faVideo, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import styles from './styles.module.css';
import BBBResourceRightColumn from './BBBResourceRightColumn';
import BBBResourceTimestamp from './BBBResourceTimestamp';
import { RecordItem } from './types';

interface OwnProps {
  closeBBBResourceMain: () => void;
  bbbResources: RecordItem[];
  isMobileDevice: boolean;
  isDarkMode: boolean;
}

interface State {
  selectedResource: RecordItem;
  bbbResourceRecords: RecordItem[];
}

class BBBResourceMain extends Component<OwnProps, State> {
  constructor(props: OwnProps) {
    super(props);
    this.state = {
      selectedResource: {
        id: props.bbbResources[0]?.id || '',
        bbbId: props.bbbResources[0]?.bbbId || '',
        contextualText: {
          description: props.bbbResources[0]?.contextualText?.description || '',
          subtitle: props.bbbResources[0]?.contextualText?.subtitle || '',
          tag: props.bbbResources[0]?.contextualText?.tag || '',
          title: props.bbbResources[0]?.contextualText?.title || '',
        },
        videoUrl: props.bbbResources[0]?.videoUrl || null,
        imageUrl: props.bbbResources[0]?.imageUrl || null,
        media: props.bbbResources[0]?.media || ('video' as const),
        status: props.bbbResources[0]?.status || '',
        startTime: props.bbbResources[0]?.startTime || 0,
        endTime: props.bbbResources[0]?.endTime || 0,
        videoFileName: props.bbbResources[0]?.videoFileName || '',
        imageFileName: props.bbbResources[0]?.imageFileName || null,
        createdDate: props.bbbResources[0]?.createdDate || '',
        lastModifiedDate: props.bbbResources[0]?.lastModifiedDate || '',
      },
      bbbResourceRecords: props.bbbResources,
    };
  }

  static getIconForMedia(media: RecordItem['media']) {
    switch (media) {
      case 'video':
        return faVideo;
      case 'image':
        return faImage;
      case 'text':
      default:
        return faFileAlt;
    }
  }

  static getCurrentVideoTime = (): number => {
    // Get the main video player element
    const mainVideoPlayer = document.querySelector(
      '#mainVideoPlayerContainer video'
    ) as HTMLVideoElement;
    if (mainVideoPlayer) {
      return mainVideoPlayer.currentTime;
    }
    return 0;
  };

  getResourceItemClassName(resource: RecordItem) {
    const { isDarkMode } = this.props;
    const { selectedResource } = this.state;

    const baseClass = styles.resourceItem;
    const darkModeClass = !isDarkMode ? styles.resourceItemDarkMode : '';
    const selectedClass = resource.id === selectedResource.id ? styles.selected : '';
    const selectedDarkClass =
      resource.id === selectedResource.id && !isDarkMode ? styles.selectedDarkMode : '';

    return `${baseClass} ${darkModeClass} ${selectedClass} ${selectedDarkClass}`.trim();
  }

  render() {
    const { selectedResource, bbbResourceRecords } = this.state;
    const { closeBBBResourceMain, isMobileDevice, isDarkMode } = this.props;

    // If no resources are available, show a message and close button
    if (!bbbResourceRecords || bbbResourceRecords.length === 0) {
      return (
        <div className={`${styles.container} ${isMobileDevice && styles.containerMobile}`}>
          <button
            type="button"
            className={`${styles.closeButton} ${!isDarkMode && styles.closeButtonDarkMode} ${
              isMobileDevice && styles.closeButtonMobile
            }`}
            onClick={closeBBBResourceMain}
          >
            ✕
          </button>
          <div className={styles.noResourcesMessage}>
            <p>No BBB resources available for the current time.</p>
          </div>
        </div>
      );
    }

    return (
      <div className={`${styles.container} ${isMobileDevice && styles.containerMobile}`}>
        <button
          type="button"
          className={`${styles.closeButton} ${
            !isDarkMode && selectedResource.media !== 'video' && styles.closeButtonDarkMode
          } ${isMobileDevice && styles.closeButtonMobile}`}
          onClick={closeBBBResourceMain}
        >
          ✕
        </button>
        <div
          className={`${styles.columnsContainer} ${
            isMobileDevice && styles.mobileColumnsContainer
          }`}
          style={{
            height: isMobileDevice ? '' : '100%',
          }}
        >
          <div
            style={{
              borderRadius: isMobileDevice ? '0 0 0 0' : '10px 0 0 10px',
              textAlign: isMobileDevice ? 'center' : 'left',
              paddingTop: isMobileDevice ? '20px' : '',
              paddingBottom: isMobileDevice ? '20px' : '',
            }}
            className={`${styles.leftColumn} ${isDarkMode && styles.leftColumnDarkMode} ${
              !isMobileDevice && styles.leftColumnDesktop
            }`}
          >
            {isMobileDevice && (
              <select
                className={styles.resourceSelect}
                value={selectedResource.id}
                onChange={e => {
                  const selected = bbbResourceRecords.find(r => r.id === e.target.value);
                  if (selected) {
                    this.setState({ selectedResource: selected });
                  }
                }}
                style={{
                  fontSize: '20px',
                  outline: 'none',
                  padding: '10px',
                  borderRadius: '10px',
                }}
              >
                {bbbResourceRecords.map(resource => (
                  <option key={resource.id} value={resource.id} style={{ fontSize: '20px' }}>
                    {resource.contextualText.title}
                  </option>
                ))}
              </select>
            )}
            {!isMobileDevice && (
              <div
                className={`${styles.resourceList} ${!isDarkMode && styles.resourceListDarkMode}`}
              >
                {bbbResourceRecords.map((resource, index) => {
                  return (
                    <React.Fragment key={resource.id}>
                      {index > 0 && <hr className={styles.divider} />}
                      <button
                        type="button"
                        className={`${styles.resourceItem} ${
                          !isDarkMode && styles.resourceItemDarkMode
                        } ${
                          selectedResource.id === resource.id &&
                          this.getResourceItemClassName(resource)
                        }`}
                        onClick={() => this.setState({ selectedResource: resource })}
                        title={
                          resource.contextualText.title &&
                          resource.contextualText.title.length >= 25
                            ? resource.contextualText.title
                            : undefined
                        }
                      >
                        <span className={styles.resourceIcon}>
                          <FontAwesomeIcon icon={BBBResourceMain.getIconForMedia(resource.media)} />
                        </span>
                        <div className={styles.resourceContent}>
                          <span className={`${styles.resourceTitle}`}>
                            {resource.contextualText.title &&
                            resource.contextualText.title.length > 30
                              ? `${resource.contextualText.title.slice(0, 27)}...`
                              : resource.contextualText.title}
                          </span>
                          {resource.startTime > 0 && resource.endTime > 0 && (
                            <BBBResourceTimestamp
                              startTime={resource.startTime}
                              endTime={resource.endTime}
                              isDarkMode={isDarkMode}
                            />
                          )}
                        </div>
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
          <div
            className={`${styles.rightColumnContainer} ${
              isDarkMode && styles.rightColumnContainerDarkMode
            } ${selectedResource.media === 'text' && styles.rightColumnContainerWithText} ${
              !isMobileDevice && styles.rightColumnContainerDesktop
            }`}
            style={{
              borderRadius: isMobileDevice ? '0 0 0 0' : '0 10px 10px 0',
              paddingTop: isMobileDevice && selectedResource.media === 'text' ? '25px' : '',
            }}
          >
            {selectedResource && (
              <BBBResourceRightColumn
                resource={selectedResource}
                isMobileDevice={isMobileDevice}
                isDarkMode={isDarkMode}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default BBBResourceMain;
